import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import ImageLightbox from "@/components/ImageLightbox";
import {
  PhotoField, WhenField, MethodChips, toNYInput, fromNYInput, nyText, money,
  methodLabels, thumbUrl, fieldInput, submitter, PAY_METHODS,
} from "@/components/ReceiptBits";
import { Plus, Receipt, Loader2, Trash2, Bell, Fuel, Search, SlidersHorizontal, X } from "lucide-react";

/**
 * FSD → Receipts. Units add the fuel they paid for (photo, when, how much,
 * which vehicle); whoever has the FSD tab marks each one paid back. The
 * person who added it is the one owed.
 */

const LAST_CAR_KEY = "fsdReceiptLastCar";
const label = "text-xs font-medium text-slate-600 dark:text-slate-300 mb-1";

// ---------- filters ----------
// Every receipt is loaded once (at most 1000) and filtered here, so every
// filter is instant and they all combine. Kept on this device between visits.
const FILTER_KEY = "fsdReceiptFilters";
const EMPTY_FILTERS = {
  status: "unpaid", search: "", cars: [], submitters: [], payers: [], methods: [],
  boughtFrom: "", boughtTo: "", paidFrom: "", paidTo: "", min: "", max: "",
  photo: "any", note: "any", sort: "newest",
};
const SORTS = [
  ["newest", "Newest bought"], ["oldest", "Oldest bought"],
  ["amount_desc", "Amount: high → low"], ["amount_asc", "Amount: low → high"],
  ["paid_newest", "Recently paid back"], ["waiting", "Waiting longest"],
];
const loadFilters = () => {
  try { return { ...EMPTY_FILTERS, ...(JSON.parse(localStorage.getItem(FILTER_KEY) || "null") || {}) }; }
  catch { return { ...EMPTY_FILTERS }; }
};
// A date input "YYYY-MM-DD" against an ISO time, in New York.
const nyDay = (iso) => {
  try { return new Date(iso).toLocaleDateString("en-CA", { timeZone: "America/New_York" }); } catch { return ""; }
};
const submitterKey = (r) => r.submitted_by_id || r.submitted_unit || r.submitted_by_name || "";

function applyFilters(rows, f) {
  const q = f.search.trim().toLowerCase();
  const min = f.min === "" ? null : Number(f.min);
  const max = f.max === "" ? null : Number(f.max);
  const out = rows.filter((r) => {
    if (f.status === "unpaid" && r.paid) return false;
    if (f.status === "paid" && !r.paid) return false;
    if (f.cars.length && !f.cars.includes(r.car_id)) return false;
    if (f.submitters.length && !f.submitters.includes(submitterKey(r))) return false;
    if (f.payers.length && !(r.paid && f.payers.includes(r.paid_by_id || r.paid_by_name))) return false;
    if (f.methods.length && !(r.paid_methods || []).some((m) => f.methods.includes(m))) return false;
    const bought = nyDay(r.purchased_at);
    if (f.boughtFrom && bought < f.boughtFrom) return false;
    if (f.boughtTo && bought > f.boughtTo) return false;
    if (f.paidFrom || f.paidTo) {
      if (!r.paid_at) return false;
      const paid = nyDay(r.paid_at);
      if (f.paidFrom && paid < f.paidFrom) return false;
      if (f.paidTo && paid > f.paidTo) return false;
    }
    const amt = Number(r.amount || 0);
    if (min != null && !Number.isNaN(min) && amt < min) return false;
    if (max != null && !Number.isNaN(max) && amt > max) return false;
    if (f.photo === "with" && !r.photo_url) return false;
    if (f.photo === "without" && r.photo_url) return false;
    if (f.note === "with" && !(r.note || r.payment_note)) return false;
    if (f.note === "without" && (r.note || r.payment_note)) return false;
    if (q) {
      const hay = [r.car_name, r.submitted_by_name, r.submitted_unit, r.paid_by_name, r.note, r.payment_note,
        money(r.amount), String(r.amount), methodLabels(r.paid_methods)].join(" ").toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });
  const t = (iso) => (iso ? new Date(iso).getTime() : 0);
  const by = {
    newest: (a, b) => t(b.purchased_at) - t(a.purchased_at),
    oldest: (a, b) => t(a.purchased_at) - t(b.purchased_at),
    amount_desc: (a, b) => Number(b.amount || 0) - Number(a.amount || 0),
    amount_asc: (a, b) => Number(a.amount || 0) - Number(b.amount || 0),
    paid_newest: (a, b) => t(b.paid_at) - t(a.paid_at),
    // Unpaid first, oldest purchase first — who's been waiting longest.
    waiting: (a, b) => (a.paid === b.paid ? t(a.purchased_at) - t(b.purchased_at) : a.paid ? 1 : -1),
  }[f.sort] || (() => 0);
  return out.sort(by);
}

export default function FuelReceipts({ cars = [], user, onCountChange }) {
  const [filters, setFilters] = useState(loadFilters);
  const [showFilters, setShowFilters] = useState(false);
  const [data, setData] = useState({ receipts: [], owed: 0, unpaid_count: 0 });
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [open, setOpen] = useState(null);       // receipt being viewed / edited
  const [photo, setPhoto] = useState(null);     // full-size photo
  const [alertsOpen, setAlertsOpen] = useState(false);

  const setF = (patch) => setFilters((f) => {
    const next = { ...f, ...patch };
    try { localStorage.setItem(FILTER_KEY, JSON.stringify(next)); } catch { /* storage off */ }
    return next;
  });

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/patrol/fuel-receipts`, { params: { status: "all" }, withCredentials: true });
      setData(r.data);
      onCountChange?.(r.data.unpaid_count || 0);
    } catch {
      toast.error("Couldn't load receipts");
    } finally {
      setLoading(false);
    }
  }, [onCountChange]);

  useEffect(() => { load(); }, [load]);

  const shown = useMemo(() => applyFilters(data.receipts, filters), [data.receipts, filters]);
  const shownTotal = shown.reduce((s, r) => s + Number(r.amount || 0), 0);
  const shownOwed = shown.filter((r) => !r.paid).reduce((s, r) => s + Number(r.amount || 0), 0);

  // The people in the filter lists come from the receipts themselves.
  const people = useMemo(() => {
    const subs = new Map(), payers = new Map();
    data.receipts.forEach((r) => {
      const k = submitterKey(r);
      if (k && !subs.has(k)) subs.set(k, submitter(r));
      const p = r.paid_by_id || r.paid_by_name;
      if (r.paid && p && !payers.has(p)) payers.set(p, r.paid_by_name || p);
    });
    const sort = (m) => [...m.entries()].sort((a, b) => String(a[1]).localeCompare(String(b[1]), undefined, { numeric: true }));
    return { submitters: sort(subs), payers: sort(payers) };
  }, [data.receipts]);

  // Active filters as removable chips (everything but status / sort / search).
  const carName = (id) => cars.find((c) => c.car_id === id)?.name || data.receipts.find((r) => r.car_id === id)?.car_name || id;
  const nameIn = (list, k) => list.find(([key]) => key === k)?.[1] || k;
  const chips = [
    ...filters.cars.map((id) => [`Vehicle: ${carName(id)}`, () => setF({ cars: filters.cars.filter((x) => x !== id) })]),
    ...filters.submitters.map((k) => [`Submitted by ${nameIn(people.submitters, k)}`, () => setF({ submitters: filters.submitters.filter((x) => x !== k) })]),
    ...filters.payers.map((k) => [`Paid back by ${nameIn(people.payers, k)}`, () => setF({ payers: filters.payers.filter((x) => x !== k) })]),
    ...filters.methods.map((k) => [`Via ${methodLabels([k])}`, () => setF({ methods: filters.methods.filter((x) => x !== k) })]),
    ...((filters.boughtFrom || filters.boughtTo) ? [[`Bought ${filters.boughtFrom || "…"} → ${filters.boughtTo || "…"}`, () => setF({ boughtFrom: "", boughtTo: "" })]] : []),
    ...((filters.paidFrom || filters.paidTo) ? [[`Paid ${filters.paidFrom || "…"} → ${filters.paidTo || "…"}`, () => setF({ paidFrom: "", paidTo: "" })]] : []),
    ...((filters.min !== "" || filters.max !== "") ? [[`Amount ${filters.min !== "" ? money(filters.min) : "…"} – ${filters.max !== "" ? money(filters.max) : "…"}`, () => setF({ min: "", max: "" })]] : []),
    ...(filters.photo !== "any" ? [[filters.photo === "with" ? "Has photo" : "No photo", () => setF({ photo: "any" })]] : []),
    ...(filters.note !== "any" ? [[filters.note === "with" ? "Has note" : "No note", () => setF({ note: "any" })]] : []),
  ];

  // The car this user is in now, else the one they used last.
  const myCar = useMemo(() => {
    const unit = user?.linked_unit;
    const inCar = unit && cars.find((c) => c.assigned_unit === unit || (c.joined_units || []).includes(unit));
    if (inCar) return inCar.car_id;
    try {
      const last = localStorage.getItem(LAST_CAR_KEY);
      if (last && cars.some((c) => c.car_id === last)) return last;
    } catch { /* ignore */ }
    return "";
  }, [cars, user]);

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <div className="inline-flex rounded-md overflow-hidden border border-slate-300 dark:border-slate-700">
          {[["unpaid", "Unpaid"], ["paid", "Paid"], ["all", "All"]].map(([k, l]) => (
            <button
              key={k}
              type="button"
              onClick={() => setF({ status: k })}
              className={`px-3 h-9 text-sm font-medium ${filters.status === k ? "bg-blue-600 text-white" : "text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800"}`}
            >
              {l}
            </button>
          ))}
        </div>
        <div className="relative flex-1 min-w-[160px] max-w-xs">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input value={filters.search} onChange={(e) => setF({ search: e.target.value })} placeholder="Search receipts…"
            className={`${fieldInput} h-9 pl-8`} />
        </div>
        <button type="button" onClick={() => setShowFilters(true)}
          className={`h-9 px-3 inline-flex items-center gap-1.5 rounded-md border text-sm font-medium ${chips.length
            ? "border-blue-500 text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-500/10"
            : "border-slate-300 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800"}`}>
          <SlidersHorizontal className="w-4 h-4" />Filters{chips.length ? ` (${chips.length})` : ""}
        </button>
        <span className="text-sm text-slate-600 dark:text-slate-300">
          Owed: <b className="text-amber-600 dark:text-amber-400">{money(data.owed)}</b>
          <span className="text-slate-400"> · {data.unpaid_count} unpaid</span>
        </span>
        <div className="ml-auto flex gap-2">
          <button type="button" onClick={() => setAlertsOpen(true)} title="Who gets told about new receipts"
            className="h-9 w-9 inline-flex items-center justify-center rounded-md border border-slate-300 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800">
            <Bell className="w-4 h-4" />
          </button>
          <button type="button" onClick={() => setAdding(true)}
            className="h-9 px-3 inline-flex items-center gap-1.5 rounded-md bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold">
            <Plus className="w-4 h-4" />Add receipt
          </button>
        </div>
      </div>

      {/* Active filters — tap × to drop one. */}
      {chips.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5">
          {chips.map(([text, clear]) => (
            <span key={text} className="inline-flex items-center gap-1 rounded-full bg-blue-50 dark:bg-blue-500/10 text-blue-700 dark:text-blue-300 ring-1 ring-blue-200 dark:ring-blue-500/30 pl-2.5 pr-1 py-0.5 text-xs font-medium">
              {text}
              <button type="button" onClick={clear} className="rounded-full p-0.5 hover:bg-blue-100 dark:hover:bg-blue-500/20" aria-label={`Remove ${text}`}>
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
          <button type="button" onClick={() => setF({ ...EMPTY_FILTERS, status: filters.status, sort: filters.sort })}
            className="text-xs font-semibold text-blue-600 dark:text-blue-400 hover:underline ml-1">Clear all</button>
        </div>
      )}
      {!loading && data.receipts.length > 0 && (
        <div className="text-xs text-slate-500 dark:text-slate-400">
          Showing <b className="text-slate-700 dark:text-slate-200">{shown.length}</b> of {data.receipts.length}
          {" · "}total {money(shownTotal)}{shownOwed > 0 ? <> · still owed <b className="text-amber-600 dark:text-amber-400">{money(shownOwed)}</b></> : null}
          {" · "}{SORTS.find(([k]) => k === filters.sort)?.[1]}
        </div>
      )}

      {loading ? (
        <div className="py-12 flex justify-center text-slate-400"><Loader2 className="w-6 h-6 animate-spin" /></div>
      ) : shown.length === 0 ? (
        <div className="py-12 text-center text-slate-500">
          <Receipt className="w-10 h-10 mx-auto mb-3 opacity-40" />
          {data.receipts.length && (chips.length || filters.search) ? "No receipts match these filters"
            : filters.status === "unpaid" ? "Nothing waiting to be paid back" : "No receipts"}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {shown.map((r) => (
            <button
              key={r.receipt_id}
              type="button"
              onClick={() => setOpen(r)}
              className="text-left flex gap-3 rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-2.5 hover:border-slate-400 dark:hover:border-slate-600"
            >
              <img src={thumbUrl(r.photo_url, 200)} alt="" className="w-16 h-16 shrink-0 rounded object-cover bg-slate-100 dark:bg-slate-800" />
              <div className="min-w-0 flex-1">
                <div className="flex items-baseline justify-between gap-2">
                  <span className="font-semibold text-slate-900 dark:text-white truncate">{r.car_name}</span>
                  <span className="font-bold text-slate-900 dark:text-white tabular-nums">{money(r.amount)}</span>
                </div>
                <div className="text-xs text-slate-500 dark:text-slate-400 truncate">
                  {nyText(r.purchased_at)} · {submitter(r)}
                </div>
                <div className="mt-1">
                  {r.paid ? (
                    <span className="inline-block rounded-full bg-emerald-100 dark:bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 text-[11px] font-semibold px-2 py-0.5">
                      Paid · {methodLabels(r.paid_methods)} · {r.paid_by_name} · {nyText(r.paid_at)}
                    </span>
                  ) : (
                    <span className="inline-block rounded-full bg-amber-100 dark:bg-amber-500/15 text-amber-700 dark:text-amber-300 text-[11px] font-semibold px-2 py-0.5">
                      Unpaid
                    </span>
                  )}
                </div>
              </div>
            </button>
          ))}
        </div>
      )}

      {showFilters && (
        <ReceiptFilters
          filters={filters}
          setF={setF}
          cars={cars.length ? cars.map((c) => [c.car_id, c.name])
            : [...new Map(data.receipts.map((r) => [r.car_id, r.car_name])).entries()]}
          people={people}
          count={shown.length}
          onClose={() => setShowFilters(false)}
        />
      )}

      {adding && (
        <ReceiptForm
          cars={cars}
          user={user}
          initial={{ car_id: myCar, when: toNYInput(), amount: "", note: "", photo_url: "" }}
          onOpenPhoto={setPhoto}
          onClose={() => setAdding(false)}
          onSaved={() => { setAdding(false); load(); }}
        />
      )}
      {open && (
        <ReceiptDetail
          receipt={open}
          cars={cars}
          user={user}
          onOpenPhoto={setPhoto}
          onClose={() => setOpen(null)}
          onChanged={(r) => { if (r) setOpen(r); else setOpen(null); load(); }}
        />
      )}
      {alertsOpen && <AlertsDialog onClose={() => setAlertsOpen(false)} />}
      {photo && <ImageLightbox src={`${photo}/thumb?size=2400`} onClose={() => setPhoto(null)} />}
    </div>
  );
}

// ---------- the Filters panel ----------
// Live: every change applies at once (the count on the button says how many
// match); Done just closes it.
function ReceiptFilters({ filters: f, setF, cars, people, count, onClose }) {
  const section = "space-y-1.5";
  const head = "text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400";
  const toggleIn = (key, v) => setF({ [key]: f[key].includes(v) ? f[key].filter((x) => x !== v) : [...f[key], v] });
  const Chips = ({ list, k, empty }) => (
    list.length === 0 ? <p className="text-xs text-slate-400">{empty}</p> : (
      <div className="flex flex-wrap gap-1.5">
        {list.map(([v, text]) => {
          const on = f[k].includes(v);
          return (
            <button key={v} type="button" onClick={() => toggleIn(k, v)}
              className={`rounded-full border px-2.5 py-1 text-xs font-medium ${on
                ? "border-blue-600 bg-blue-600 text-white"
                : "border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800"}`}>
              {text}
            </button>
          );
        })}
      </div>
    )
  );
  const Seg = ({ k, options }) => (
    <div className="inline-flex rounded-md overflow-hidden border border-slate-300 dark:border-slate-700">
      {options.map(([v, text]) => (
        <button key={v} type="button" onClick={() => setF({ [k]: v })}
          className={`px-2.5 h-8 text-xs font-medium ${f[k] === v ? "bg-blue-600 text-white" : "text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800"}`}>
          {text}
        </button>
      ))}
    </div>
  );
  const dateCls = `${fieldInput} h-9`;
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg max-h-[90vh] p-0 gap-0 flex flex-col" onOpenAutoFocus={(e) => e.preventDefault()}>
        <DialogHeader className="px-5 pt-5 pb-3 border-b border-slate-200 dark:border-slate-800">
          <DialogTitle className="flex items-center gap-2"><SlidersHorizontal className="w-4 h-4" />Filter receipts</DialogTitle>
        </DialogHeader>
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">
          <div className={section}>
            <div className={head}>Status</div>
            <Seg k="status" options={[["unpaid", "Unpaid"], ["paid", "Paid"], ["all", "All"]]} />
          </div>
          <div className={section}>
            <div className={head}>Sort</div>
            <select value={f.sort} onChange={(e) => setF({ sort: e.target.value })} className={`${fieldInput} h-9`}>
              {SORTS.map(([v, t]) => <option key={v} value={v}>{t}</option>)}
            </select>
          </div>
          <div className={section}>
            <div className={head}>Submitted by (owed to)</div>
            <Chips list={people.submitters} k="submitters" empty="No receipts yet" />
          </div>
          <div className={section}>
            <div className={head}>Paid back by</div>
            <Chips list={people.payers} k="payers" empty="Nobody has paid one back yet" />
          </div>
          <div className={section}>
            <div className={head}>Vehicle</div>
            <Chips list={cars} k="cars" empty="No vehicles" />
          </div>
          <div className={section}>
            <div className={head}>Paid back with</div>
            <Chips list={PAY_METHODS.map((m) => [m.key, m.label])} k="methods" empty="" />
          </div>
          <div className={section}>
            <div className={head}>Bought between</div>
            <div className="grid grid-cols-2 gap-2">
              <input type="date" value={f.boughtFrom} onChange={(e) => setF({ boughtFrom: e.target.value })} className={dateCls} aria-label="Bought from" />
              <input type="date" value={f.boughtTo} onChange={(e) => setF({ boughtTo: e.target.value })} className={dateCls} aria-label="Bought to" />
            </div>
          </div>
          <div className={section}>
            <div className={head}>Paid back between</div>
            <div className="grid grid-cols-2 gap-2">
              <input type="date" value={f.paidFrom} onChange={(e) => setF({ paidFrom: e.target.value })} className={dateCls} aria-label="Paid from" />
              <input type="date" value={f.paidTo} onChange={(e) => setF({ paidTo: e.target.value })} className={dateCls} aria-label="Paid to" />
            </div>
          </div>
          <div className={section}>
            <div className={head}>Amount</div>
            <div className="grid grid-cols-2 gap-2">
              <input type="number" inputMode="decimal" min="0" step="0.01" placeholder="Min $" value={f.min}
                onChange={(e) => setF({ min: e.target.value })} className={`${fieldInput} h-9`} />
              <input type="number" inputMode="decimal" min="0" step="0.01" placeholder="Max $" value={f.max}
                onChange={(e) => setF({ max: e.target.value })} className={`${fieldInput} h-9`} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className={section}>
              <div className={head}>Photo</div>
              <Seg k="photo" options={[["any", "Any"], ["with", "Has"], ["without", "None"]]} />
            </div>
            <div className={section}>
              <div className={head}>Note</div>
              <Seg k="note" options={[["any", "Any"], ["with", "Has"], ["without", "None"]]} />
            </div>
          </div>
        </div>
        <div className="flex gap-2 border-t border-slate-200 dark:border-slate-800 px-5 py-3">
          <button type="button" onClick={() => setF({ ...EMPTY_FILTERS, status: "all" })}
            className="h-10 px-4 rounded-lg border border-slate-300 dark:border-slate-600 text-sm font-medium text-slate-700 dark:text-slate-200">Reset</button>
          <button type="button" onClick={onClose}
            className="flex-1 h-10 rounded-lg bg-blue-600 hover:bg-blue-700 text-sm font-semibold text-white">
            Show {count} receipt{count === 1 ? "" : "s"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function CarSelect({ cars, value, onChange }) {
  return (
    <div>
      <div className={label}>Vehicle</div>
      <select value={value} onChange={(e) => onChange(e.target.value)} className={fieldInput}>
        <option value="">Pick the vehicle…</option>
        {cars.map((c) => <option key={c.car_id} value={c.car_id}>{c.name}</option>)}
      </select>
    </div>
  );
}

function AmountField({ value, onChange, title = "Amount" }) {
  return (
    <div>
      <div className={label}>{title}</div>
      <div className="relative">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">$</span>
        <input type="text" inputMode="decimal" placeholder="0.00" value={value}
          onChange={(e) => onChange(e.target.value.replace(/[^0-9.]/g, ""))}
          className={`${fieldInput} pl-7`} />
      </div>
    </div>
  );
}

/** Add a receipt (or, with `receipt`, edit one). */
function ReceiptForm({ cars, user, initial, receipt, onOpenPhoto, onClose, onSaved }) {
  const [f, setF] = useState(initial);
  const [saving, setSaving] = useState(false);
  const set = (k) => (v) => setF((x) => ({ ...x, [k]: v }));

  const save = async () => {
    if (!f.photo_url) return toast.error("Add a photo of the receipt");
    if (!f.car_id) return toast.error("Pick the vehicle");
    if (!(parseFloat(f.amount) > 0)) return toast.error("Enter the amount");
    setSaving(true);
    const body = {
      car_id: f.car_id, photo_url: f.photo_url, amount: parseFloat(f.amount),
      purchased_at: fromNYInput(f.when), note: f.note,
    };
    try {
      const r = receipt
        ? await axios.put(`${API}/patrol/fuel-receipts/${receipt.receipt_id}`, body, { withCredentials: true })
        : await axios.post(`${API}/patrol/fuel-receipts`, body, { withCredentials: true });
      try { localStorage.setItem(LAST_CAR_KEY, f.car_id); } catch { /* ignore */ }
      toast.success(receipt ? "Receipt updated" : "Receipt added");
      onSaved(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Fuel className="w-5 h-5" />{receipt ? "Edit receipt" : "Add fuel receipt"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <PhotoField label="Receipt photo" value={f.photo_url} onChange={set("photo_url")} onOpen={onOpenPhoto} />
          <CarSelect cars={cars} value={f.car_id} onChange={set("car_id")} />
          <WhenField label="Bought" value={f.when} onChange={set("when")} />
          <AmountField value={f.amount} onChange={set("amount")} />
          <div>
            <div className={label}>Submitted by</div>
            <div className="text-sm text-slate-900 dark:text-slate-100">
              {receipt ? submitter(receipt) : (user?.linked_unit || user?.name)}
              <span className="text-xs text-slate-500"> — the one paid back</span>
            </div>
          </div>
          <div>
            <div className={label}>Note (optional)</div>
            <textarea rows={2} value={f.note} onChange={(e) => set("note")(e.target.value)} className={`${fieldInput} h-auto py-2`} />
          </div>
          <div className="flex gap-2 pt-1">
            <button type="button" onClick={onClose} className="flex-1 h-10 rounded-md border border-slate-300 dark:border-slate-700 text-sm font-medium text-slate-700 dark:text-slate-200">Cancel</button>
            <button type="button" disabled={saving} onClick={save} className="flex-1 h-10 rounded-md bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white text-sm font-semibold inline-flex items-center justify-center gap-1.5">
              {saving && <Loader2 className="w-4 h-4 animate-spin" />}{receipt ? "Save" : "Add receipt"}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ReceiptDetail({ receipt: r, cars, user, onOpenPhoto, onClose, onChanged }) {
  const [editing, setEditing] = useState(false);
  const [confirmDel, setConfirmDel] = useState(false);
  const [methods, setMethods] = useState(r.paid_methods || []);
  const [paidOn, setPaidOn] = useState(toNYInput());
  const [note, setNote] = useState(r.payment_note || "");
  const [busy, setBusy] = useState(false);

  const pay = async (paid) => {
    if (paid && methods.length === 0) return toast.error("Pick how it was paid");
    setBusy(true);
    try {
      const res = await axios.post(`${API}/patrol/fuel-receipts/${r.receipt_id}/payment`,
        { paid, methods, paid_at: fromNYInput(paidOn), note }, { withCredentials: true });
      toast.success(paid ? "Marked paid" : "Marked unpaid");
      onChanged(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save");
    } finally {
      setBusy(false);
    }
  };

  const del = async () => {
    try {
      await axios.delete(`${API}/patrol/fuel-receipts/${r.receipt_id}`, { withCredentials: true });
      toast.success("Receipt deleted");
      onChanged(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't delete");
    }
  };

  if (editing) {
    return (
      <ReceiptForm
        cars={cars}
        user={user}
        receipt={r}
        initial={{ car_id: r.car_id, when: toNYInput(new Date(r.purchased_at)), amount: String(r.amount), note: r.note || "", photo_url: r.photo_url }}
        onOpenPhoto={onOpenPhoto}
        onClose={() => setEditing(false)}
        onSaved={(nr) => { setEditing(false); onChanged(nr); }}
      />
    );
  }

  return (
    <>
      <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Fuel className="w-5 h-5" />{r.car_name} · {money(r.amount)}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <button type="button" onClick={() => onOpenPhoto(r.photo_url)} className="block">
              <img src={thumbUrl(r.photo_url, 600)} alt="Receipt" className="max-h-56 rounded-md border border-slate-200 dark:border-slate-700 object-contain bg-slate-50 dark:bg-slate-950" />
            </button>
            <div className="grid grid-cols-[6.5rem_1fr] gap-y-1 text-slate-700 dark:text-slate-200">
              <span className="text-slate-500">Bought</span><span>{nyText(r.purchased_at)}</span>
              <span className="text-slate-500">Submitted by</span><span>{submitter(r)}</span>
              {r.note && (<><span className="text-slate-500">Note</span><span className="break-words">{r.note}</span></>)}
            </div>

            <div className="rounded-lg border border-slate-200 dark:border-slate-700 p-3 space-y-2">
              {r.paid ? (
                <>
                  <div className="font-semibold text-emerald-700 dark:text-emerald-400">Paid back</div>
                  <div className="text-slate-700 dark:text-slate-200">
                    {methodLabels(r.paid_methods)} · {nyText(r.paid_at)} · by {r.paid_by_name}
                  </div>
                  {r.payment_note && <div className="text-slate-500 break-words">{r.payment_note}</div>}
                  <button type="button" disabled={busy} onClick={() => pay(false)}
                    className="h-9 px-3 rounded-md border border-slate-300 dark:border-slate-700 text-sm font-medium text-slate-700 dark:text-slate-200">
                    Mark unpaid
                  </button>
                </>
              ) : (
                <>
                  <div className="font-semibold text-slate-900 dark:text-white">Mark paid back to {submitter(r)}</div>
                  <div className={label}>How</div>
                  <MethodChips value={methods} onChange={setMethods} />
                  <WhenField label="Paid on" value={paidOn} onChange={setPaidOn} />
                  <div>
                    <div className={label}>Note (optional)</div>
                    <input value={note} onChange={(e) => setNote(e.target.value)} className={fieldInput} />
                  </div>
                  <div className="text-xs text-slate-500">Paid by: {user?.name}</div>
                  <button type="button" disabled={busy} onClick={() => pay(true)}
                    className="w-full h-10 rounded-md bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white text-sm font-semibold inline-flex items-center justify-center gap-1.5">
                    {busy && <Loader2 className="w-4 h-4 animate-spin" />}Mark paid
                  </button>
                </>
              )}
            </div>

            <div className="flex gap-2">
              <button type="button" onClick={() => setEditing(true)}
                className="flex-1 h-10 rounded-md border border-slate-300 dark:border-slate-700 text-sm font-medium text-slate-700 dark:text-slate-200">
                Edit
              </button>
              <button type="button" onClick={() => setConfirmDel(true)}
                className="h-10 px-3 rounded-md border border-red-300 dark:border-red-500/50 text-sm font-medium text-red-600 dark:text-red-400 inline-flex items-center gap-1">
                <Trash2 className="w-4 h-4" />Delete
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      <AlertDialog open={confirmDel} onOpenChange={setConfirmDel}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this receipt?</AlertDialogTitle>
            <AlertDialogDescription>{r.car_name} · {money(r.amount)} from {submitter(r)}. This can't be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={del} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

/** Who hears about a new receipt: an email list, and (when it's working) one WhatsApp group. */
export function AlertsDialog({ onClose, endpoint = "/patrol/fuel-receipt-alerts", what = "fuel receipt" }) {
  const [cfg, setCfg] = useState(null);
  const [emails, setEmails] = useState("");
  const [groups, setGroups] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}${endpoint}`, { withCredentials: true })
      .then((r) => { setCfg(r.data); setEmails((r.data.emails || []).join(", ")); })
      .catch(() => setCfg({ emails: [], whatsapp_enabled: false, whatsapp_group: "" }));
    axios.get(`${API}/audit/whatsapp/bot-groups`, { withCredentials: true })
      .then((r) => setGroups(r.data?.groups || []))
      .catch(() => setGroups([]));
  }, [endpoint]);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}${endpoint}`, {
        emails: emails.split(/[\s,;]+/).filter(Boolean),
        whatsapp_enabled: cfg.whatsapp_enabled,
        whatsapp_group: cfg.whatsapp_group || "",
      }, { withCredentials: true });
      toast.success("Saved");
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Bell className="w-5 h-5" />New {what} alerts</DialogTitle>
        </DialogHeader>
        {!cfg ? (
          <div className="py-8 flex justify-center"><Loader2 className="w-5 h-5 animate-spin" /></div>
        ) : (
          <div className="space-y-4 text-sm">
            <div>
              <div className={label}>Email these addresses</div>
              <textarea rows={2} value={emails} onChange={(e) => setEmails(e.target.value)}
                placeholder="name@example.com, other@example.com" className={`${fieldInput} h-auto py-2`} />
            </div>
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-slate-700 dark:text-slate-200">
                <input type="checkbox" checked={!!cfg.whatsapp_enabled}
                  onChange={(e) => setCfg((c) => ({ ...c, whatsapp_enabled: e.target.checked }))} />
                Also post in a WhatsApp group
              </label>
              {cfg.whatsapp_enabled && (
                <select value={cfg.whatsapp_group || ""} onChange={(e) => setCfg((c) => ({ ...c, whatsapp_group: e.target.value }))} className={fieldInput}>
                  <option value="">Pick the group…</option>
                  {groups.filter((g) => (g.id || g.jid || "").endsWith("@g.us")).map((g) => (
                    <option key={g.id || g.jid} value={g.id || g.jid}>{g.subject || g.name || g.id || g.jid}</option>
                  ))}
                  {cfg.whatsapp_group && !groups.some((g) => (g.id || g.jid) === cfg.whatsapp_group) && (
                    <option value={cfg.whatsapp_group}>{cfg.whatsapp_group}</option>
                  )}
                </select>
              )}
            </div>
            <div className="flex gap-2">
              <button type="button" onClick={onClose} className="flex-1 h-10 rounded-md border border-slate-300 dark:border-slate-700 font-medium text-slate-700 dark:text-slate-200">Cancel</button>
              <button type="button" disabled={saving} onClick={save} className="flex-1 h-10 rounded-md bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white font-semibold">Save</button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
